/**
 * PILOT-3 (GDPR): Privacy Notice Page
 *
 * Accessible at all times from the consent banner and guest UI.
 * Rendered as a full-screen overlay (no router required).
 *
 * Covers GDPR Article 13 transparency obligations:
 * - What data is collected and why
 * - Retention period (7 days)
 * - Who has access
 * - How to request deletion or a copy
 */

import { motion } from 'framer-motion';

interface PrivacyPageProps {
  onClose: () => void;
}

export function PrivacyPage({ onClose }: PrivacyPageProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-60 bg-black/95 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Privacy Notice"
    >
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold text-white">Privacy Notice</h1>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-sm underline"
            aria-label="Close privacy notice"
          >
            ← Back
          </button>
        </div>

        <div className="space-y-6 text-sm text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-white mb-2">Who we are</h2>
            <p>
              ZAXIA is the digital concierge system operated by this hotel. When you use
              the concierge to make requests or bookings, we process personal data on
              behalf of the hotel in accordance with GDPR.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">What data we collect</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Your name and room number (provided by the hotel at check-in)</li>
              <li>Service requests you submit (e.g. housekeeping, room service, bookings)</li>
              <li>Time and content of your requests</li>
              <li>Any preferences you share (e.g. Green Stay eco preferences)</li>
            </ul>
            <p className="mt-2">
              We do <strong className="text-white">not</strong> collect payment card data,
              passport numbers, or device identifiers.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Why we collect it</h2>
            <p>
              Your data is used solely to fulfil your concierge requests and improve service
              delivery during your stay. The legal basis for processing is your explicit consent
              (GDPR Article 6(1)(a)), given via the consent screen before your first request.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">How long we keep it</h2>
            <p>
              All activity data is automatically deleted after <strong className="text-white">7 days</strong>.
              No data about your stay is retained after this window closes.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Who has access</h2>
            <p>
              Only authorised hotel staff (managers and supervisors) can view your request
              history. Data is never sold, shared with third parties, or used for marketing.
            </p>
            <p className="mt-2">
              The system is hosted on Railway (Google Cloud, Frankfurt region),
              which processes data under a Data Processing Agreement compliant with
              GDPR Article 28.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Your rights</h2>
            <p>Under GDPR you have the right to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong className="text-white">Access</strong> — request a copy of all data held about you</li>
              <li><strong className="text-white">Erasure</strong> — request immediate deletion of all your data</li>
              <li><strong className="text-white">Rectification</strong> — correct any inaccurate data</li>
              <li><strong className="text-white">Portability</strong> — receive your data in machine-readable format</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, please speak to hotel reception. We will
              respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Withdrawing consent</h2>
            <p>
              You may withdraw consent at any time by speaking to hotel reception. Withdrawal
              does not affect the lawfulness of processing before withdrawal.
            </p>
          </section>

          <section className="border-t border-white/10 pt-4 text-xs text-gray-500">
            <p>Last updated: May 2026 · Version 1.0</p>
          </section>
        </div>

        <div className="mt-8">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </motion.div>
  );
}
